import type { Matrix } from './geometry';
import { damage } from './damage';
import { changed, noOp, rejected, type Command, type CommandExecution } from './commands';
import { createId, findLayer, walkLayers, type BlendMode, type EditorDocument, type Layer } from './model';

export const LAYER_COMPS_EXTENSION = 'maipo:layer-comps';

export interface LayerCompAppearance {
  visible: boolean;
  opacity: number;
  fillOpacity: number;
  blendMode: BlendMode;
  transform: Matrix;
}

export interface LayerComp {
  id: string;
  name: string;
  createdAt: string;
  layers: Record<string, LayerCompAppearance>;
}

function validComp(value: unknown): value is LayerComp {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<LayerComp>;
  return typeof candidate.id === 'string'
    && typeof candidate.name === 'string'
    && typeof candidate.createdAt === 'string'
    && Boolean(candidate.layers && typeof candidate.layers === 'object');
}

export function layerComps(document: EditorDocument): LayerComp[] {
  const value = document.extensions[LAYER_COMPS_EXTENSION];
  return Array.isArray(value) ? value.filter(validComp).map((comp) => structuredClone(comp)) : [];
}

export function captureLayerComp(document: EditorDocument, name: string): LayerComp {
  return {
    id: createId('comp'),
    name: name.trim() || `Comp ${layerComps(document).length + 1}`,
    createdAt: new Date().toISOString(),
    layers: Object.fromEntries(walkLayers(document.root).map((layer) => [layer.id, {
      visible: layer.visible,
      opacity: layer.opacity,
      fillOpacity: layer.fillOpacity,
      blendMode: layer.blendMode,
      transform: [...layer.transform] as Matrix,
    }])),
  };
}

export class SetLayerCompsCommand implements Command {
  readonly label: string;
  private before?: unknown;
  private hadBefore = false;

  constructor(private readonly comps: readonly LayerComp[], label = 'Update layer comps') {
    this.label = label;
  }

  retainedBytes(): number {
    return 128 + JSON.stringify(this.comps).length * 2 + JSON.stringify(this.before ?? null).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    const next = structuredClone(this.comps);
    const current = layerComps(document);
    if (JSON.stringify(current) === JSON.stringify(next)) return noOp();
    if (this.before === undefined) {
      this.hadBefore = Object.hasOwn(document.extensions, LAYER_COMPS_EXTENSION);
      this.before = structuredClone(document.extensions[LAYER_COMPS_EXTENSION] ?? null);
    }
    if (next.length === 0) delete document.extensions[LAYER_COMPS_EXTENSION];
    else document.extensions[LAYER_COMPS_EXTENSION] = next;
    return changed();
  }

  undo(document: EditorDocument): void {
    if (this.hadBefore) document.extensions[LAYER_COMPS_EXTENSION] = structuredClone(this.before);
    else delete document.extensions[LAYER_COMPS_EXTENSION];
  }
}

interface LayerSnapshot {
  id: string;
  appearance: LayerCompAppearance;
}

function appearanceOf(layer: Layer): LayerCompAppearance {
  return {
    visible: layer.visible,
    opacity: layer.opacity,
    fillOpacity: layer.fillOpacity,
    blendMode: layer.blendMode,
    transform: [...layer.transform] as Matrix,
  };
}

function applyAppearance(layer: Layer, appearance: LayerCompAppearance): void {
  layer.visible = appearance.visible;
  layer.opacity = appearance.opacity;
  layer.fillOpacity = appearance.fillOpacity;
  layer.blendMode = appearance.blendMode;
  layer.transform = [...appearance.transform] as Matrix;
}

export class ApplyLayerCompCommand implements Command {
  readonly label: string;
  private before: LayerSnapshot[] = [];

  constructor(private readonly comp: LayerComp) {
    this.label = `Apply comp “${comp.name}”`;
  }

  retainedBytes(): number {
    return 128 + JSON.stringify(this.comp).length * 2 + JSON.stringify(this.before).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    const targets = Object.entries(this.comp.layers)
      .map(([id, appearance]) => ({ layer: findLayer(document.root, id), appearance }))
      .filter((entry): entry is { layer: Layer; appearance: LayerCompAppearance } => Boolean(entry.layer));
    if (targets.length === 0) return rejected('None of this comp’s layers exist in the current document.');
    this.before = targets.map(({ layer }) => ({ id: layer.id, appearance: appearanceOf(layer) }));
    const changedTargets = targets.filter(({ layer, appearance }) => JSON.stringify(appearanceOf(layer)) !== JSON.stringify(appearance));
    if (changedTargets.length === 0) return noOp();
    for (const { layer, appearance } of changedTargets) applyAppearance(layer, appearance);
    return changed(damage({ fullDocument: true }));
  }

  undo(document: EditorDocument): void {
    for (const snapshot of this.before) {
      const layer = findLayer(document.root, snapshot.id);
      if (layer) applyAppearance(layer, snapshot.appearance);
    }
  }
}
